import React, { useCallback, useEffect, useState } from 'react';
import { MessageSquare, RefreshCw, User, Phone, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '../services/api';

const formatWhen = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const contactLabel = (row) => {
  const parts = [row.contactFirstName, row.contactLastName].filter(Boolean);
  if (parts.length) return parts.join(' ');
  return 'Unknown contact';
};

const SmsRepliesPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState({ items: [], total: 0, page: 1, limit: 25, totalPages: 1 });
  const limit = 25;

  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await api.smsInbox.list({ page, limit });
      setData(res);
    } catch (e) {
      setError(e.message || 'Could not load SMS replies.');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    load(1);
  }, [load]);

  const { items, total, page, totalPages } = data;

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">SMS replies</h1>
          <p className="mt-1 text-gray-600">
            Inbound texts from people who received your messages (via your Twilio webhook).
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(page)}
          disabled={loading}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          <MessageSquare size={18} className="text-indigo-600" />
          <span>
            {loading ? 'Loading…' : `${total.toLocaleString()} message${total === 1 ? '' : 's'}`}
          </span>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {!loading && !items.length && !error && (
          <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 px-6 py-12 text-center text-gray-600">
            <p className="font-medium text-gray-800">No replies yet</p>
            <p className="mt-2 text-sm">
              Saving <strong className="font-semibold text-gray-800">Settings</strong> updates your Messaging Service
              inbound + status callback URLs when Twilio credentials and an <code className="text-gray-800">MG…</code>{' '}
              service SID are set. Or configure the same URL manually in Twilio:{' '}
              <code className="rounded bg-white px-1.5 py-0.5 text-xs text-gray-800">…/api/webhooks/twilio/sms</code>.
            </p>
          </div>
        )}

        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <th className="pb-3 pr-4">When</th>
                  <th className="pb-3 pr-4">From</th>
                  <th className="pb-3 pr-4">Contact</th>
                  <th className="pb-3">Message</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((row) => (
                  <tr key={row.id} className="align-top text-gray-800">
                    <td className="py-4 pr-4 whitespace-nowrap text-gray-600">{formatWhen(row.createdAt)}</td>
                    <td className="py-4 pr-4">
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs sm:text-sm">
                        <Phone size={14} className="shrink-0 text-gray-400" />
                        {row.fromPhone}
                      </span>
                    </td>
                    <td className="py-4 pr-4">
                      <span className="inline-flex items-center gap-1.5">
                        <User size={14} className="shrink-0 text-gray-400" />
                        <span className="font-medium">{contactLabel(row)}</span>
                      </span>
                    </td>
                    <td className="py-4 max-w-md">
                      <p className="whitespace-pre-wrap break-words text-gray-800">{row.body}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={page <= 1 || loading}
                onClick={() => load(page - 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft size={18} />
                Previous
              </button>
              <button
                type="button"
                disabled={page >= totalPages || loading}
                onClick={() => load(page + 1)}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                Next
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsRepliesPage;
